import type { DocumentsGridPreset } from '@/domain/documents/schemas';

// Pure cache merges for the grid presets list. The save and delete answers were never recorded on dev; the legacy
// reducer took either the whole new list or one preset, so both are handled.

type SaveAnswer = DocumentsGridPreset | DocumentsGridPreset[] | null | undefined;

// A list answer replaces the cached list; a single preset is upserted by id.
export const mergeSavedPreset = (list: DocumentsGridPreset[], answer: SaveAnswer): DocumentsGridPreset[] => {
  if (!answer) return list;
  if (Array.isArray(answer)) return answer;
  return list.some(({ id }) => id === answer.id)
    ? list.map((preset) => (preset.id === answer.id ? answer : preset))
    : [...list, answer];
};

// A list answer replaces the cached list; anything else drops the deleted id.
export const mergeDeletedPreset = (list: DocumentsGridPreset[], answer: SaveAnswer, id: string) =>
  Array.isArray(answer) ? answer : list.filter((preset) => preset.id !== id);

// The preset a save produced: the single answer, or — in a list answer — the one with the saved id, or for a new
// preset the one the list did not have before.
export const savedPresetOf = (
  answer: SaveAnswer,
  { id, name }: { id?: string | null; name: string },
  before: ReadonlyArray<DocumentsGridPreset>,
): DocumentsGridPreset | null => {
  if (!answer) return null;
  if (!Array.isArray(answer)) return answer;
  if (id) return answer.find((preset) => preset.id === id) ?? null;
  const known = new Set(before.map((preset) => preset.id));
  return answer.find((preset) => !known.has(preset.id)) ?? answer.find((preset) => preset.name === name) ?? null;
};
